import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, functions } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateEmail,
  verifyBeforeUpdateEmail as firebaseVerifyBeforeUpdateEmail,
  updatePassword,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [loading, setLoading] = useState(true);

  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  function sendVerificationEmail() {
    return sendEmailVerification(auth.currentUser);
  }

  function resetPassword(email) {
    const configuredUrl = process.env.REACT_APP_PASSWORD_RESET_REDIRECT_URL;
    const fallbackUrl = `https://${window.location.hostname === 'localhost'
      ? 'yamanoboro-28798.firebaseapp.com'
      : window.location.hostname}${process.env.PUBLIC_URL || ''}/login`;

    return sendPasswordResetEmail(auth, email, {
      url: configuredUrl || fallbackUrl,
      handleCodeInApp: false,
    });
  }

  function updateUserEmail(email) {
    return updateEmail(currentUser, email);
  }

  function verifyBeforeUpdateEmail(email) {
    return firebaseVerifyBeforeUpdateEmail(currentUser, email);
  }

  function updateUserPassword(password) {
    return updatePassword(currentUser, password);
  }

  function reauthenticateUser(password) {
    const credential = EmailAuthProvider.credential(
      currentUser.email,
      password
    );
    return reauthenticateWithCredential(currentUser, credential);
  }

  function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }

  async function deleteAccount(password) {
    // 再認証（セキュリティ確認）
    const providerId = currentUser.providerData[0]?.providerId;
    if (providerId === 'google.com') {
      const provider = new GoogleAuthProvider();
      await reauthenticateWithPopup(currentUser, provider);
    } else {
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
    }
    // Cloud Function 経由で Firestore データ + Firebase Auth を完全削除
    const deleteAccountFn = httpsCallable(functions, 'deleteAccount');
    await deleteAccountFn({});
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    sendVerificationEmail,
    resetPassword,
    updateUserEmail,
    verifyBeforeUpdateEmail,
    updateUserPassword,
    reauthenticateUser,
    signInWithGoogle,
    deleteAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
